import { computed, reactive, ComputedRef } from "vue";

type ValidationFunction<T> = (value: T) => string | null;

interface FormOptions<T> {
  initialValues: T;
  validationSchema?: Partial<Record<keyof T, ValidationFunction<T[keyof T]>>>;
  onSubmit: (values: T) => void | Promise<void>;
}

interface FormState<T> {
  values: T;
  errors: { [K in keyof T]?: string };
  touched: { [K in keyof T]?: boolean };
  focused: { [K in keyof T]?: boolean };
  isSubmitting: boolean;
}
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export function useForm<T extends Record<string, any>>(
  options: FormOptions<T>
): {
  values: T;
  errors: { [K in keyof T]?: string };
  touched: { [K in keyof T]?: boolean };
  focused: { [K in keyof T]?: boolean };
  isSubmitting: boolean;
  isValid: ComputedRef<boolean>;
  handleChange: (event: Event) => void;
  handleBlur: (event: Event) => void;
  handleSubmit: (event?: Event) => Promise<void>;
  setValue: (name: keyof T, value: T[keyof T]) => void;
  resetForm: () => void;
  validateForm: () => boolean;
  validateField: (name: keyof T) => void;
  handleFocus: (event: Event) => void;
} {
  const { initialValues, validationSchema = {}, onSubmit } = options;

  const formState = reactive<FormState<T>>({
    values: { ...initialValues },
    errors: {},
    touched: {},
    focused: {},
    isSubmitting: false,
  });

  const isValid = computed(
    () => Object.keys(formState.errors as Record<string, string>).length === 0
  );

  const validateField = (name: keyof T) => {
    if (
      !validationSchema ||
      !(validationSchema as Record<keyof T, ValidationFunction<T[keyof T]>>)[
        name
      ]
    )
      return;

    const validator = (
      validationSchema as Record<keyof T, ValidationFunction<T[keyof T]>>
    )[name];
    const errorMessage = validator(
      (formState.values as Record<keyof T, T[keyof T]>)[name]
    );
    if (errorMessage) {
      (formState.errors as Record<keyof T, string>)[name] = errorMessage;
    } else {
      delete (formState.errors as Record<keyof T, string>)[name];
    }
  };

  const validateForm = () => {
    Object.keys(formState.values).forEach((key) => {
      const fieldName = key as keyof T;
      validateField(fieldName);
    });

    return isValid.value;
  };

  const handleChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const name = target.name as keyof T;
    const value = target.type === "checkbox" ? target.checked : target.value;

    validateForm();

    (formState.values as Record<keyof T, T[keyof T]>)[name] =
      value as T[keyof T];

    if ((formState.touched as Record<keyof T, boolean>)[name]) {
      validateField(name);
    }
  };

  const setValue = (name: keyof T, value: T[keyof T]) => {
    (formState.values as Record<keyof T, T[keyof T]>)[name] = value;
    if ((formState.touched as Record<keyof T, boolean>)[name]) {
      validateField(name);
    }
  };

  const handleFocus = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const name = target.name as keyof T;

    (formState.focused as Record<keyof T, boolean>)[name] = true;
  };

  const handleBlur = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const name = target.name as keyof T;

    (formState.touched as Record<keyof T, boolean>)[name] = true;
    (formState.focused as Record<keyof T, boolean>)[name] = false;
    validateField(name);
  };

  const handleSubmit = async (event?: Event) => {
    if (event) event.preventDefault();

    Object.keys(formState.values).forEach((key) => {
      (formState.touched as Record<keyof T, boolean>)[key as keyof T] = true;
    });

    const valid = validateForm();

    if (valid) {
      formState.isSubmitting = true;
      try {
        await onSubmit(formState.values as T);
      } finally {
        formState.isSubmitting = false;
      }
    }
  };

  const resetForm = () => {
    Object.keys(initialValues).forEach((key) => {
      (formState.values as Record<keyof T, T[keyof T]>)[key as keyof T] =
        initialValues[key as keyof T];
    });

    Object.keys(formState.errors as Record<string, string>).forEach((key) => {
      delete (formState.errors as Record<keyof T, string>)[key as keyof T];
    });

    Object.keys(formState.touched as Record<string, boolean>).forEach((key) => {
      delete (formState.touched as Record<keyof T, boolean>)[key as keyof T];
    });

    formState.isSubmitting = false;

    validateForm();
  };

  return {
    ...formState,
    errors: formState.errors as { [K in keyof T]?: string | undefined },
    touched: formState.touched as { [K in keyof T]?: boolean | undefined },
    focused: formState.focused as { [K in keyof T]?: boolean | undefined },
    values: formState.values as T,
    handleChange,
    handleBlur,
    handleSubmit,
    handleFocus,
    setValue,
    resetForm,
    validateForm,
    validateField,
    isValid,
  };
}
